import React from 'react';
import { Container, Section, SectionHeader, Kicker, PullQuote, StatBlock, Reveal } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { Photo } from '../components/ui/Photo';
import { PhotoStrip } from '../components/sections/PhotoStrip';
import { PHOTOS } from '../data/photos';
const PRINCIPLES = [{
  title: 'Evidence before opinion',
  detail: 'We state what we know, how we know it and how confident we are. Where the evidence is thin we say so rather than filling the gap with confidence.'
}, {
  title: 'A person stays accountable',
  detail: 'Every system we build has a named reviewer and a route of appeal. Augmented intelligence means the machine assists the judgement, it does not replace the accountability.'
}, {
  title: 'Capability, not dependency',
  detail: 'We hand over the method, not just the output. If your team cannot repeat the work after we leave, we have not finished.'
}, {
  title: 'Scope agreed in writing',
  detail: 'Every engagement starts with a discovery call and a written proposal setting out scope, timing and fees. Nothing begins before that is agreed.'
}];
export function About() {
  return <>
      <section className="surface-dark" data-id="element-1440" data-mp="23jrp44fa6dj1">
        <Container className="py-16 sm:py-24" data-id="element-1441" data-mp="1yjn7uc6jterr">
          <Kicker data-id="element-1442" data-mp="1yhtyuq8gqigl">About</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1443" data-mp="2dgnyecd073on">
            A consulting firm built for the decision, not the deck
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1444" data-mp="28fqhs47n6hp9">
            Cavalla Solution advises organisations across Africa and globally on market intelligence, data led
            growth and augmented intelligence. We were founded on a simple frustration: too much analysis arrives
            after the decision has been made, and too little of it changes anything.
          </p>
        </Container>
      </section>

      <Section aria-labelledby="story" data-id="element-1445" data-mp="1ouwj7saqebwk">
        <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr]" data-id="element-1446" data-mp="1ovxxe5n1neii">
          <div data-id="element-1447" data-mp="1svq7ims35spz">
            <SectionHeader id="story" kicker="Why we exist" title="Made for people, powered by AI" data-id="element-1448" data-mp="1sx9m88y3fvpn" />
            <div className="mt-6 max-w-prose space-y-5 text-[16px] text-body" data-id="element-1449" data-mp="1xu9rwir16wg8">
              <p data-id="element-1450" data-mp="22mkvpwow72e2">
                The tools available to an organisation in Accra, Nairobi or Lagos are now the same tools available
                to one in Frankfurt. What differs is the surrounding capability: agreed data, trained people, clear
                governance and the confidence to act on an imperfect answer.
              </p>
              <p data-id="element-1451" data-mp="2gmlth39v1tpb">
                That gap is the work. We close it by pairing machine analysis with human judgement, and by handing
                over the method so the value does not leave when we do.
              </p>
              <p data-id="element-1452" data-mp="ma5sagvx95rb">
                We serve NGOs, government, banks, health providers, agricultural businesses and small and medium
                enterprises. The sectors differ. The pattern rarely does.
              </p>
            </div>
          </div>
          <div className="space-y-10" data-id="element-1453" data-mp="evv4ipcy1cs9">
            <Photo photo={PHOTOS.friendsInternational} aspect="aspect-[4/3]" data-id="element-1454" data-mp="eutsahk6yk8y" />
            <PullQuote data-id="element-1455" data-mp="9xtmm7r97jid">
              A report that does not change a decision has failed, however well it is written.
            </PullQuote>
          </div>
        </div>
      </Section>

      <PhotoStrip id="people" title="Time with the people closest to the problem" intro="Our team, the leaders we work alongside, and partners from Liberia to Ethiopia. Most of what we know, we learned in meetings like these." photos={[PHOTOS.teamOutdoor, PHOTOS.liberia, PHOTOS.ethiopiaKit]} data-id="element-1456" data-mp="12lpf109sip9c" />

      <Section tone="mist" aria-labelledby="principles" data-id="element-1457" data-mp="oosokzxl39sz">
        <SectionHeader id="principles" kicker="How we work" title="Four principles we hold to" data-id="element-1458" data-mp="3df1zseb1dv6r" />
        <div className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-1459" data-mp="39bvy7jyvcs6m">
          {PRINCIPLES.map((principle, index) => <Reveal key={principle.title} delay={index * 0.05} data-id="element-1460" data-mp="112eernjgwpjr" data-mpi={index}>
              <div className="h-full border-l-2 border-gold bg-white p-6" data-id="element-1461" data-mp="15uz4acvccghh">
                <h3 className="font-display text-[21px] leading-snug text-ink" data-id="element-1462" data-mp="r5t3wlkrok80">{principle.title}</h3>
                <p className="mt-3 text-[15px] text-body" data-id="element-1463" data-mp="w4mm2rb00zei">{principle.detail}</p>
              </div>
            </Reveal>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="numbers" data-id="element-1464" data-mp="1jv9vjh0kbx36">
        <SectionHeader id="numbers" kicker="Our first ninety days" title="What we are building towards" data-id="element-1465" data-mp="1ottinkqw7ex4" />
        <div className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1466" data-mp="1auk5yv1wb2xw">
          <StatBlock value={10} label="Priority sectors under continuous coverage" data-id="element-1467" data-mp="1ev41ju6tlyhx" />
          <StatBlock value={3} label="Service lines, each with three ways to engage" data-id="element-1468" data-mp="3vk13n3zg0x9l" />
          <StatBlock value={4} label="Steps in the method, run on every engagement" data-id="element-1469" data-mp="3vm40o7z0jqnt" />
          <StatBlock value={30} suffix=" min" label="Discovery call, with the founder" data-id="element-1470" data-mp="3hjr295j76t0" />
        </div>
        <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-1471" data-mp="3s093mf9hifuz">
          <Button to="/contact" size="lg" data-id="element-1472" data-mp="3n1z63y0af367">Book a discovery call</Button>
          <Button to="/services" variant="secondary" size="lg" data-id="element-1473" data-mp="5ulxvgzqki3o">See our services</Button>
        </div>
      </Section>
    </>;
}